import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Switch,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Chip,
    Divider,
    Paper
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    CloudUpload as CloudIcon,
    Save as SaveIcon,
    PlayArrow as RunIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import dataManagementService from '../../../services/dataManagementService';
import useNotificationStore from '../../../stores/notificationStore';

const BackupConfiguration = () => {
    const { showSuccess, showError } = useNotificationStore();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [config, setConfig] = useState({
        enabled: false,
        scheduleType: 'daily',
        scheduleTime: {
            hour: 2,
            minute: 0,
            dayOfWeek: 0,
            dayOfMonth: 1
        },
        retentionPolicy: {
            localRetentionDays: 7,
            s3RetentionDays: 30,
            maxLocalBackups: 10
        },
        s3Config: {
            enabled: false,
            bucketPath: 'backups'
        }
    });
    const [status, setStatus] = useState(null);

    useEffect(() => {
        loadConfiguration();
        loadStatus();
    }, []);

    const loadConfiguration = async () => {
        try {
            setLoading(true);
            const response = await dataManagementService.getBackupConfig();
            if (response.data) {
                setConfig(response.data);
            }
        } catch (error) {
            showError('Failed to load backup configuration');
        } finally {
            setLoading(false);
        }
    };

    const loadStatus = async () => {
        try {
            const response = await dataManagementService.getScheduleStatus();
            setStatus(response.data);
        } catch (error) {
            console.error('Failed to load status:', error);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await dataManagementService.updateBackupConfig(config);
            showSuccess('Backup configuration saved successfully');
            await loadStatus();
        } catch (error) {
            showError('Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        try {
            setTesting(true);
            const response = await dataManagementService.testBackupConfig();
            showSuccess(response.message || 'Test backup created successfully');
        } catch (error) {
            showError('Test backup failed');
        } finally {
            setTesting(false);
        }
    };

    const handleTriggerBackup = async () => {
        try {
            const response = await dataManagementService.triggerManualBackup();
            showSuccess(response.message || 'Backup triggered successfully');
        } catch (error) {
            showError('Failed to trigger backup');
        }
    };

    const formatNextBackupTime = (time) => {
        if (!time) return 'Not scheduled';
        return new Date(time).toLocaleString();
    };

    const formatLastBackupStatus = (lastBackup) => {
        if (!lastBackup) return null;
        
        return (
            <Box display="flex" alignItems="center" gap={1}>
                {lastBackup.status === 'success' ? (
                    <SuccessIcon color="success" fontSize="small" />
                ) : (
                    <ErrorIcon color="error" fontSize="small" />
                )}
                <Typography variant="body2">
                    Last backup: {new Date(lastBackup.timestamp).toLocaleString()}
                    {lastBackup.error && ` - ${lastBackup.error}`}
                </Typography>
            </Box>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Automatic Backup Configuration
                </Typography>
                
                {status && (
                    <Box mb={3}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Scheduler Status
                                </Typography>
                                <Chip
                                    label={status.schedulerRunning ? 'Running' : 'Stopped'}
                                    color={status.schedulerRunning ? 'success' : 'default'}
                                    size="small"
                                />
                            </Box>
                            {status.lastBackup && formatLastBackupStatus(status.lastBackup)}
                            <Typography variant="body2" color="text.secondary" mt={1}>
                                Next backup: {formatNextBackupTime(status.nextBackupTime)}
                            </Typography>
                        </Paper>
                    </Box>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.enabled}
                                    onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                                />
                            }
                            label="Enable Automatic Backups"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth disabled={!config.enabled}>
                            <InputLabel>Schedule Type</InputLabel>
                            <Select
                                value={config.scheduleType}
                                onChange={(e) => setConfig({ ...config, scheduleType: e.target.value })}
                                label="Schedule Type"
                            >
                                <MenuItem value="hourly">Hourly</MenuItem>
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box display="flex" gap={2}>
                            <TextField
                                label="Hour"
                                type="number"
                                value={config.scheduleTime.hour}
                                onChange={(e) => setConfig({
                                    ...config,
                                    scheduleTime: { ...config.scheduleTime, hour: parseInt(e.target.value) }
                                })}
                                inputProps={{ min: 0, max: 23 }}
                                disabled={!config.enabled || config.scheduleType === 'hourly'}
                                fullWidth
                            />
                            <TextField
                                label="Minute"
                                type="number"
                                value={config.scheduleTime.minute}
                                onChange={(e) => setConfig({
                                    ...config,
                                    scheduleTime: { ...config.scheduleTime, minute: parseInt(e.target.value) }
                                })}
                                inputProps={{ min: 0, max: 59 }}
                                disabled={!config.enabled}
                                fullWidth
                            />
                        </Box>
                    </Grid>

                    {config.scheduleType === 'weekly' && (
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth disabled={!config.enabled}>
                                <InputLabel>Day of Week</InputLabel>
                                <Select
                                    value={config.scheduleTime.dayOfWeek}
                                    onChange={(e) => setConfig({
                                        ...config,
                                        scheduleTime: { ...config.scheduleTime, dayOfWeek: parseInt(e.target.value) }
                                    })}
                                    label="Day of Week"
                                >
                                    <MenuItem value={0}>Sunday</MenuItem>
                                    <MenuItem value={1}>Monday</MenuItem>
                                    <MenuItem value={2}>Tuesday</MenuItem>
                                    <MenuItem value={3}>Wednesday</MenuItem>
                                    <MenuItem value={4}>Thursday</MenuItem>
                                    <MenuItem value={5}>Friday</MenuItem>
                                    <MenuItem value={6}>Saturday</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {config.scheduleType === 'monthly' && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Day of Month"
                                type="number"
                                value={config.scheduleTime.dayOfMonth}
                                onChange={(e) => setConfig({
                                    ...config,
                                    scheduleTime: { ...config.scheduleTime, dayOfMonth: parseInt(e.target.value) }
                                })}
                                inputProps={{ min: 1, max: 31 }}
                                disabled={!config.enabled}
                                fullWidth
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" gutterBottom>
                            Retention Policy
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Local Retention (days)"
                            type="number"
                            value={config.retentionPolicy.localRetentionDays}
                            onChange={(e) => setConfig({
                                ...config,
                                retentionPolicy: { ...config.retentionPolicy, localRetentionDays: parseInt(e.target.value) }
                            })}
                            inputProps={{ min: 1 }}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label="S3 Retention (days)"
                            type="number"
                            value={config.retentionPolicy.s3RetentionDays}
                            onChange={(e) => setConfig({
                                ...config,
                                retentionPolicy: { ...config.retentionPolicy, s3RetentionDays: parseInt(e.target.value) }
                            })}
                            inputProps={{ min: 1 }}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Max Local Backups"
                            type="number"
                            value={config.retentionPolicy.maxLocalBackups}
                            onChange={(e) => setConfig({
                                ...config,
                                retentionPolicy: { ...config.retentionPolicy, maxLocalBackups: parseInt(e.target.value) }
                            })}
                            inputProps={{ min: 1 }}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" gutterBottom>
                            Cloud Storage (S3)
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.s3Config.enabled}
                                    onChange={(e) => setConfig({
                                        ...config,
                                        s3Config: { ...config.s3Config, enabled: e.target.checked }
                                    })}
                                />
                            }
                            label="Enable S3 Upload"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="S3 Bucket Path"
                            value={config.s3Config.bucketPath}
                            onChange={(e) => setConfig({
                                ...config,
                                s3Config: { ...config.s3Config, bucketPath: e.target.value }
                            })}
                            disabled={!config.s3Config.enabled}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" gap={2} mt={2}>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Configuration'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ScheduleIcon />}
                                onClick={handleTest}
                                disabled={testing}
                            >
                                {testing ? 'Testing...' : 'Test Configuration'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RunIcon />}
                                onClick={handleTriggerBackup}
                                disabled={!config.enabled}
                            >
                                Trigger Backup Now
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                        Automatic backups will include all database collections and can be configured to upload to S3 for offsite storage.
                        Manual backups can still be created from the main backup section.
                    </Typography>
                </Alert>
            </CardContent>
        </Card>
    );
};

export default BackupConfiguration;